import { ReactNode, Suspense } from 'react';

import { Skeleton } from '@iwoca/orion';

import { useSuspenseGetSharedFunctionality } from '../../api/lending/lapiHooks.suspense';
import { CustomErrorBoundary } from '../../AppWrapper';

type BannerFunctionality = {
  name: ReactNode;
  enabled: boolean;
};

export const FunctionalityWidget = () => {
  const loadingState = [
    { name: <Skeleton size={100} />, enabled: false },
    { name: <Skeleton size={140} />, enabled: false },
    { name: <Skeleton size={80} />, enabled: false },
    { name: <Skeleton size={200} />, enabled: false },
    { name: <Skeleton size={210} />, enabled: false },
    { name: <Skeleton size={100} />, enabled: false },
    { name: <Skeleton size={140} />, enabled: false },
    { name: <Skeleton size={80} />, enabled: false },
    { name: <Skeleton size={200} />, enabled: false },
  ];

  const ErrorComponent = () => {
    return (
      <div className="border-b-[1px] border-black/10 px-s pb-l pt-l">
        <h2 className="m-0 text-m">User functionality</h2>
        <p>Unable to load functionalities.</p>
      </div>
    );
  };

  return (
    // eslint-disable-next-line react-hooks/static-components -- simple inline error component
    <CustomErrorBoundary errorComponent={<ErrorComponent />}>
      <Suspense
        fallback={<FunctionalityWidgetShell functionalities={loadingState} />}
      >
        <FunctionalityWidgetInner />
      </Suspense>
    </CustomErrorBoundary>
  );
};

const FunctionalityWidgetInner = () => {
  const { functionality } = useSuspenseGetSharedFunctionality();

  const result = Object.entries(functionality)
    .map(([name, enabled]) => ({
      name,
      enabled,
    }))
    .filter((functionality) => !functionality.name.includes('switch')); // switches are generally global, so not worth showing as not tied to actual user-unique behaviour

  return <FunctionalityWidgetShell functionalities={result} />;
};

const TrafficLight = ({ value }: { value: boolean }) => {
  return (
    <div
      className={`h-[10px] w-[10px] rounded-2xl ${value ? 'bg-success-mid' : 'bg-error'}`}
    ></div>
  );
};

const FunctionalityWidgetShell = ({
  functionalities,
}: {
  functionalities: BannerFunctionality[];
}) => {
  return (
    <div className="px-s pt-l">
      <h2 className="m-0 pb-s text-m">User functionality</h2>
      <div className="m-0 border-b-[1px] border-black/10 px-0 pb-l">
        {functionalities.map((functionality, i) => (
          <div
            className="grid grid-cols-[fit-content(100%)_1fr_10px] items-center gap-s"
            key={i}
          >
            {functionality.name}
            <div>
              <hr className="h-[1px] border-t-[1px] border-black/15" />
            </div>
            <div>
              <TrafficLight value={functionality.enabled} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
